import {useEffect, useRef, useState} from 'react';

const LINE_CLAMP = 2; // 2줄 처리
const ELLIPSIS = '...'; // 말줄임 처리

// css -webkit-line-clamp를 사용하지 않고 말줄임하는 hook
// ex. text: 가나다라마바사 -> 가나다라마바...
// ex. text: 가나다라마바사, appendText: 더보기 -> 가나다라마바... 더보기
export const useClampLine = ({text, appendText = ''}: {text: string; appendText?: string}) => {
  const [clampText, setClampText] = useState(text);
  const nodeRef = useRef<HTMLElement>(null);
  const maxHeightRef = useRef(1);

  const isOverflow = () => {
    return nodeRef.current ? nodeRef.current.clientHeight > maxHeightRef.current : false;
  };

  const getTruncatedText = (endIndex: number) => {
    return text.slice(0, endIndex) + (endIndex < text.length ? ELLIPSIS : '') + appendText;
  };

  const clampLines = () => {
    if (!nodeRef.current) return;

    const style = getComputedStyle(nodeRef.current);
    const lineHeight = parseInt(style.lineHeight, 10);
    maxHeightRef.current = lineHeight * LINE_CLAMP;

    let start = 0;
    let end = text.length;
    let mid = 0;

    while (start <= end) {
      mid = Math.floor((start + end) / 2);
      const truncatedText = getTruncatedText(mid);
      nodeRef.current.innerText = truncatedText;

      if (isOverflow()) {
        end = mid - 1;
      } else {
        start = mid + 1;
      }
    }

    const finalText = getTruncatedText(end);
    nodeRef.current.innerText = finalText;
    setClampText(finalText);
  };

  // iOS에서만 발생하는 오류 대응
  // ResizeObserver loop completed with undelivered notifications
  const animationFrame = (fn: () => void) => {
    let id: number | null = null;

    return () => {
      if (id !== null) {
        cancelAnimationFrame(id);
      }

      id = requestAnimationFrame(() => {
        fn();
        id = null;
      });
    };
  };

  useEffect(() => {
    const resizeObserver = new ResizeObserver(animationFrame(clampLines));

    if (nodeRef.current) {
      resizeObserver.observe(nodeRef.current);
    }

    return () => resizeObserver.disconnect();
  }, [text, appendText]);

  return [nodeRef, {clampText}] as const;
};
